import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/InstitutePage.css";
import Footer from "../FooterBn";
import Hero from "../RatingComponentBn";
import Leaders from "../FeaturedITBn";

const API_URL = "https://server.goatteachers.com/api/institutes";

const InstitutePage = () => {
  const navigate = useNavigate();

  const [institutes, setInstitutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // fetch once
  useEffect(() => {
    let alive = true;

    const fetchInstitutes = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const res = await axios.get(API_URL);
        const list = Array.isArray(res?.data?.institutes)
          ? res.data.institutes
          : [];

        if (!alive) return;
        setInstitutes(list);
      } catch (err) {
        if (!alive) return;
        setErrorMsg("Failed to load institutes. Please try again.");
        console.error("Error fetching institutes:", err);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchInstitutes();
    return () => {
      alive = false;
    };
  }, []);

  // derived values
  const totalInstitutes = institutes.length;

  const filteredInstitutes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return institutes;

    return institutes.filter((it) =>
      (it?.institute_name || "").toLowerCase().includes(q),
    );
  }, [institutes, searchQuery]);

  const handleInstituteClick = useCallback(
    (instituteId) => {
      navigate(`/institute/${instituteId}`);
      window.scrollTo(0, 0);
    },
    [navigate],
  );

  return (
    <>
      <section className="institute-page-section">
        <div className="institute-page">
          <Hero />
          <Leaders />

          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 pb-16">
            {/* header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-center text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                  Live Universities
                </h1>
                <p className="text-center mt-1 text-sm sm:text-base text-slate-300">
                  Browse institutes and open a profile to see details.
                </p>
              </div>

              <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm sm:text-base font-semibold text-white backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-[#25d976]" />
                <span className="text-white/90">
                  {totalInstitutes} Universities Found
                </span>
              </div>
            </div>

            {/* search */}
            {/* <div className="mt-6">
              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <svg
                  className="h-5 w-5 text-white/50"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10 4a6 6 0 1 1 0 12A6 6 0 0 1 10 4m0-2a8 8 0 1 0 4.9 14.32l4.39 4.39a1 1 0 0 0 1.42-1.42l-4.39-4.39A8 8 0 0 0 10 2z" />
                </svg>

                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search universities..."
                  className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                />

                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 hover:bg-white/10"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div> */}

            {/* states */}
            {loading ? (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
                <p className="text-base font-semibold text-white">
                  Loading universities…
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  Please wait a moment.
                </p>
              </div>
            ) : errorMsg ? (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
                <p className="text-base font-semibold text-white">{errorMsg}</p>
                <p className="mt-2 text-sm text-slate-300">
                  Check your API server and refresh.
                </p>
              </div>
            ) : (
              <>
                {/* grid */}
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredInstitutes.length > 0 ? (
                    filteredInstitutes.map((institute) => (
                      <button
                        key={institute.institute_id}
                        type="button"
                        onClick={() =>
                          handleInstituteClick(institute.institute_id)
                        }
                        onTouchStart={(e) => e.currentTarget.focus()}
                        className="
                          group w-full text-left rounded-2xl border border-white/10 bg-white/5 p-5
                          transition-all duration-300
                          hover:-translate-y-1 hover:border-transparent hover:bg-[#25d976]
                          hover:shadow-[0_18px_60px_-18px_rgba(37,217,118,0.55)]
                          active:-translate-y-1 active:bg-[#25d976]
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25d976]/60
                          focus:bg-[#25d976]
                        "
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {/* NAME: Desktop hover/focus scroll, Mobile auto-scroll */}
                            <h3 className="text-lg font-extrabold text-white transition-colors group-hover:text-[#07130d] group-focus:text-[#07130d] group-active:text-[#07130d]">
                              <span className="relative block overflow-hidden whitespace-nowrap">
                                <span
                                  className="
                                    inline-block will-change-transform
                                    motion-reduce:transform-none motion-reduce:animate-none
                                    animate-[marquee_10s_linear_infinite]
                                    md:animate-none
                                    group-hover:md:animate-[marquee_8s_linear_infinite]
                                    group-focus:md:animate-[marquee_8s_linear_infinite]
                                    group-active:md:animate-[marquee_8s_linear_infinite]
                                  "
                                >
                                  {institute.institute_name}
                                  <span className="mx-10" aria-hidden="true">
                                    {institute.institute_name}
                                  </span>
                                </span>
                              </span>
                            </h3>

                            <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-1 text-xs sm:text-sm font-semibold text-slate-200 transition-colors group-hover:border-black/20 group-hover:bg-white/20 group-hover:text-[#07130d] group-focus:border-black/20 group-focus:bg-white/20 group-focus:text-[#07130d] group-active:border-black/20 group-active:bg-white/20 group-active:text-[#07130d]">
                              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 group-hover:bg-[#07130d] group-focus:bg-[#07130d] group-active:bg-[#07130d]" />
                              <span className="truncate">
                                {institute.location || "Location not listed"}
                              </span>
                            </div>
                          </div>

                          {/* chevron */}
                          <div className="shrink-0 rounded-full border border-white/10 bg-white/5 p-2 text-white/80 transition-all group-hover:border-black/20 group-hover:bg-white/20 group-hover:text-[#07130d] group-focus:border-black/20 group-focus:bg-white/20 group-focus:text-[#07130d] group-active:border-black/20 group-active:bg-white/20 group-active:text-[#07130d]">
                            <svg
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-4 w-4"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.21 14.77a.75.75 0 0 1 .02-1.06L10.94 10 7.23 6.29a.75.75 0 1 1 1.06-1.06l4.24 4.24a.75.75 0 0 1 0 1.06l-4.24 4.24a.75.75 0 0 1-1.06.02Z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>

                        <div className="mt-5 flex items-center justify-between">
                          <p className="text-xs sm:text-sm text-slate-300 transition-colors group-hover:text-[#07130d] group-focus:text-[#07130d] group-active:text-[#07130d]">
                            Tap to view institute profile
                          </p>

                          <span className="text-sm font-extrabold text-white/90 transition-all group-hover:text-[#07130d] group-hover:translate-x-1 group-focus:text-[#07130d] group-focus:translate-x-1 group-active:text-[#07130d] group-active:translate-x-1">
                            Open →
                          </span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="col-span-full rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
                      <p className="text-base font-semibold text-white">
                        No institutes found.
                      </p>
                      <p className="mt-2 text-sm text-slate-300">
                        Try a different keyword.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default InstitutePage;
